import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

function OrderShareDialog({
  isOpen,
  onClose,
  orders = [],
  title,
  tills = [],
  tillsLoading = false,
  confirmLoading = false,
  onConfirm,
}) {
  const [selectedTills, setSelectedTills] = useState([]);

  const tillOptions = useMemo(
    () =>
      (tills || []).map((till) => ({
        id: String(till.id),
        label: till.device_name,
      })),
    [tills],
  );

  const initialSelectedTillIds = useMemo(() => {
    if ((orders || []).length > 1) {
      return [];
    }

    const mergedTillIds = (orders || []).flatMap((order) => {
      if (Array.isArray(order?.sharedTillIds) && order.sharedTillIds.length) {
        return order.sharedTillIds;
      }

      return order?.tillId ? [order.tillId] : [];
    });

    return Array.from(new Set(mergedTillIds.map((id) => String(id))));
  }, [orders]);

  useEffect(() => {
    if (!isOpen) return;
    setSelectedTills(initialSelectedTillIds);
  }, [initialSelectedTillIds, isOpen]);

  const toggleTill = (tillId, checked) => {
    setSelectedTills((prev) => {
      if (checked) {
        return prev.includes(tillId) ? prev : [...prev, tillId];
      }

      return prev.filter((id) => id !== tillId);
    });
  };

  const isConfirmDisabled = orders.length === 0;

  const handleConfirm = async () => {
    if (!onConfirm) return;

    await onConfirm({
      orderIds: orders.map((order) => order?.id).filter(Boolean),
      tillIds: selectedTills.map((id) => Number(id)).filter(Boolean),
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-h-[85vh] max-w-[530px] overflow-hidden text-white">
        <DialogTitle className="px-4 pt-4 text-center font-medium text-white">
          Order Share (Till : {title || "1245787"})
        </DialogTitle>

        <div className="max-h-[calc(85vh-84px)] space-y-4 overflow-y-auto px-4 pb-4 pt-4">
          <div className="space-y-2">
            <div>
              <h3 className="mb-2 px-3 text-sm font-medium">Selected Orders</h3>
            </div>
            <div className="grid max-h-[320px] gap-2 overflow-y-auto pr-1">
              {orders?.map((order) => (
                <Card
                  key={order?.id}
                  className="border-0 bg-[#2d2d2d] text-white shadow-none"
                >
                  <CardContent className="grid gap-x-4 gap-y-1 p-4 text-[13px] md:grid-cols-2">
                    <div className="space-y-1">
                      <p>Order ID: {order?.id || order?.orderId}</p>
                      <p>Customer Name: {order?.customerName}</p>
                      <p>Tel: {order?.customerPhone}</p>
                    </div>
                    <div className="space-y-1 md:text-right">
                      <p className="font-semibold text-cyellow">
                        {order?.orderType}
                      </p>
                      <p>Date: {order?.dateLabel}</p>
                      <p>Accepted : {order?.accepted}</p>
                      <p>Dispatch:{order?.dispatch}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="max-h-[220px] overflow-y-auto pt-3">
              <div>
                <h3 className="mb-2 px-3 text-sm font-medium">
                  Select Tills to Share
                </h3>
              </div>
              {tillsLoading ? (
                <p className="px-3 py-2 text-sm text-cwhite3">
                  Loading tills...
                </p>
              ) : (
                <div className="grid gap-3 md:grid-cols-2">
                  {tillOptions.map((option) => (
                    <label
                      key={option.id}
                      className="flex items-center gap-3 rounded-md px-3 py-2 text-sm text-white"
                    >
                      <Checkbox
                        checked={selectedTills.includes(option.id)}
                        onCheckedChange={(checked) =>
                          toggleTill(option.id, Boolean(checked))
                        }
                        className="border-gray-500 data-[state=checked]:border-cyellow data-[state=checked]:bg-cyellow data-[state=checked]:text-black"
                      />
                      <span className="text-[14px]">{option.label}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>

          {!tillsLoading && tillOptions.length === 0 && (
            <p className="text-center text-xs text-cwhite3">No till found</p>
          )}

          <Button
            onClick={handleConfirm}
            isLoading={confirmLoading}
            disabled={isConfirmDisabled || confirmLoading}
            className="w-full bg-cgreen text-[15px] text-white hover:bg-cgreen/90"
          >
            Confirm
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default OrderShareDialog;
